import React, { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import CPPUtils from 'services/CPPUtils';
import './style.scss';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultButton from 'components/Utils/NewDefaultButton';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { store, useAppSelector } from 'careand-redux/store';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import PharmacySearch from 'components/Pharmacy/PharmacySearch';
import { useDefaultPharmacy } from 'hooks/useDefaultPharmacy';
import { useIsLoading, useNavStack, useNavTab } from 'careand-redux/slices/ui/hooks';
import { CheckGreen } from 'scenes/PatientRegistration/PersonalDetails';
import CalendarUtils from 'services/CalendarUtils';
import DefaultConfirmationModal, {
  ConfirmationContent,
  DefaultConfirmationModalProps,
} from 'components/Utils/DefaultConfirmationScreen';
import { PatientMedication, PharmacyDirectory, Prescription } from 'generated/api';
import { Stacks, Tabs } from 'careand-redux/slices/ui/navigation';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';

type BooleanMap = Partial<Record<string, boolean>>;

const DEFAULT_ARRAY: PatientMedication[] = [];

const PrescriptionRefill = ({
  prescription,
  onSuccess,
}: {
  prescription: Prescription;
  onSuccess: () => void;
}) => {
  const dispatch = useAppDispatch();

  const { patient_id } = prescription;

  const patient = useAppSelector((state) => state.patients.byId[patient_id]);
  const apptNav = useNavStack(Stacks.Appointments);
  const tabNav = useNavTab();

  const loading = useIsLoading(api.HealthRecords.addPrescriptionRefill);

  const defaultPharmacy = useDefaultPharmacy(patient);
  const userPickedPharmacy = useRef(false);

  const [note, setNote] = useState('');
  // Seed with the prescription's pharmacy; once the patient's default pharmacy
  // resolves from the directory, prefer it (unless the user already picked one).
  const [pharmacy, setPharmacy] = useState(prescription.pharmacy);

  useEffect(() => {
    if (defaultPharmacy && !userPickedPharmacy.current) setPharmacy(defaultPharmacy);
  }, [defaultPharmacy]);
  const [pharmacyScreen, setPharmacyScreen] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [showRefillDisabledModal, setShowRefillDisabledModal] = useState(false);
  const [showStillHasMedicationModal, setShowStillHasMedicationModal] = useState<
    Payload | undefined
  >();

  const allMedications =
    useAppSelector((state) => state.healthRecords.byId[patient_id]?.healthRecords?.medications) ||
    DEFAULT_ARRAY;

  const activeMedications = useMemo(() => {
    let active = CPPUtils.getActiveMedications(allMedications);
    return active.filter((med) => med.history?.[0]?.value?.status === 'Prescribed');
  }, [allMedications]);

  const prescriptionMedicationNames = prescription.medications.map((m) => m.name);

  const prescriptionMedications = activeMedications.filter((medication) =>
    prescriptionMedicationNames.includes(medication.value.name),
  );

  const [checked, setChecked] = useState(
    _(prescriptionMedications)
      .filter(
        (it) =>
          !CPPUtils.medicationStillLasts(it, -3)[0] &&
          !it.value.prescription_medication?.disable_refills,
      )
      .groupBy((it: PatientMedication) => it.value?.name)
      .mapValues(() => true)
      .value(),
  );

  const otherMedications = activeMedications.filter(
    (medication) => !prescriptionMedicationNames.includes(medication.value.name),
  );

  async function submitRefill() {
    let medications = activeMedications
      .filter((medication) => checked[medication.value.name])
      .map((medication) => medication.value.prescription_medication || medication.value);

    let prescriptionRefill = {
      patient_id: patient_id,
      medications,
      note,
      pharmacy,
      prescription_id: prescription.id!,
    };

    const result = await dispatch(api.HealthRecords.addPrescriptionRefill({ prescriptionRefill }), {
      errorTitle: 'Prescription Refill',
    });

    if (result) setSuccessful(true);
  }

  const noMedicationsSelected = useMemo(() => {
    return Object.values(checked).filter((value) => !!value).length === 0;
  }, [checked]);

  const requestDisabled = noMedicationsSelected || !pharmacy;

  function onClick(medication: PatientMedication) {
    const { value } = medication;
    const key = value?.name || value?.prescription_medication?.name || '';
    const disable_refills = value.prescription_medication?.disable_refills;

    const [stillHasMedication, lastsUntil] = CPPUtils.medicationStillLasts(medication, -3);

    let pharmacy: PharmacyDirectory | undefined = undefined;
    {
      const state = store.getState();
      const prescriptions = state.healthRecords.byId[patient_id]?.prescriptions;
      const prescription = prescriptions?.find(
        (it) => it.id === medication.value.prescription_medication?.document_id,
      );
      pharmacy = prescription?.pharmacy;
    }

    if (disable_refills) {
      setShowRefillDisabledModal(true);
    } else if (stillHasMedication) {
      setShowStillHasMedicationModal({
        lastsUntil: lastsUntil,
        refills: _.toInteger(value?.prescription_medication?.refills),
        pharmacy,
      });
    } else {
      setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  }

  function onClickBook() {
    apptNav.popToRoot();
    apptNav.push('/booking/profile');
    tabNav.switchTab(Tabs.Appointments);
  }

  return (
    <div className="prescription-refill">
      {successful ? (
        <SuccessfulScreen onClick={onSuccess} />
      ) : (
        <>
          <RefillsDisabledDialog
            open={showRefillDisabledModal}
            onConfirm={() => setShowRefillDisabledModal(false)}
            onClickBook={onClickBook}
          />
          <StillHasMedicationDialog
            medicationData={showStillHasMedicationModal}
            onConfirm={() => setShowStillHasMedicationModal(undefined)}
            onClickBook={onClickBook}
          />
          <span className="top-title">Select which medications you need a refill for</span>
          <MedicationList
            medications={prescriptionMedications}
            title="This prescription:"
            checked={checked}
            onClick={onClick}
          />
          <MedicationList
            medications={otherMedications}
            title="Other prescriptions:"
            checked={checked}
            onClick={onClick}
            showDate={true}
          />
          <span className="medication-title">Pharmacy</span>
          <div className="pharmacy-section">
            {!!pharmacy && <PharmacyBadge pharmacy={pharmacy} showMap={false} />}
            {!pharmacy && <span>No pharmacy selected</span>}
            <DefaultButton
              name={!pharmacy ? 'Choose Pharmacy' : 'Change Pharmacy'}
              className={!pharmacy ? 'blue' : 'light'}
              onClick={() => setPharmacyScreen(true)}
              style={{ width: '100%' }}
            />
            <DefaultPatientModal
              className="modal pharmacy-modal"
              title="Choose a Pharmacy"
              wrap={true}
              fullHeight={false}
              padding={true}
              content={
                <div className="pharmacy-search">
                  <PharmacySearch
                    showMap={false}
                    onSelect={(pharmacy) => {
                      setPharmacyScreen(false);
                      userPickedPharmacy.current = true;
                      setPharmacy(pharmacy);
                    }}
                    initialPharmacies={pharmacy ? [pharmacy] : undefined}
                  />
                </div>
              }
              isOpen={pharmacyScreen}
              onRequestClose={() => setPharmacyScreen(false)}
            />
          </div>
          <span className="top-title">
            If necessary, you can write a small note for your practitioner
          </span>
          <TextareaAutosize
            name="note"
            value={note}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNote(event.target.value)}
          />

          <ConfirmationButton
            name="Request Refill"
            className="blue"
            loading={loading}
            disabled={requestDisabled}
            onClick={submitRefill}
            style={{ width: '100%' }}
            confirmation={{
              message:
                'Please allow 24 to 48 hours for your practitioner to respond to your refill request.',
              confirmLabel: 'I Understand',
            }}
          />
        </>
      )}
    </div>
  );
};

type Provided = 'title' | 'text' | 'icon' | 'confirmLabel' | 'cancelLabel';
function RefillsDisabledDialog(
  props: Omit<DefaultConfirmationModalProps, Provided> & { onClickBook: () => void },
) {
  return (
    <DefaultConfirmationModal
      {...props}
      title="No Refills"
      text="This medication requires a follow-up appointment in order to get a refill."
      confirmLabel="OK"
      cancelLabel="Book Appointment"
      onCancel={props.onClickBook}
      icon="prescription-bottle"
    />
  );
}

type Payload = {
  lastsUntil: number;
  refills?: number;
  pharmacy?: PharmacyDirectory;
};
function StillHasMedicationDialog(
  props: Omit<DefaultConfirmationModalProps, Provided | 'open'> & {
    medicationData?: Payload;
    onClickBook: () => void;
  },
) {
  let text: ReactNode = null;
  if (props.medicationData) {
    const refillsStr =
      (props.medicationData.refills && (
        <span>
          This medication was prescribed with{' '}
          <b>
            {props.medicationData.refills} refill{props.medicationData.refills === 1 ? '' : 's'}
          </b>
          .<br />
        </span>
      )) ||
      null;
    const datePart = (
      <span>
        {props.medicationData.refills ? 'It' : 'This medication'} should last until{' '}
        <b>{CalendarUtils.format(props.medicationData.lastsUntil)}</b>.
      </span>
    );
    const rest: ReactNode = (
      <>
        <span className="inline-block mb-4">
          If you have run out of your medication, but still have refills left, you can get them at
          your pharmacy. Alternatively, book an appointment.
        </span>
        {props.medicationData.pharmacy && (
          <PharmacyBadge pharmacy={props.medicationData.pharmacy} />
        )}
      </>
    );

    text = (
      <>
        {refillsStr} {datePart}
        <br />
        <br />
        {rest}
      </>
    );
  }
  return (
    <DefaultConfirmationModal
      {...props}
      open={!!props.medicationData}
      icon="prescription-bottle"
      title="No Refills"
      text={text}
      confirmLabel="OK"
      cancelLabel="Book Appointment"
      onCancel={props.onClickBook}
    />
  );
}

const MedicationList = ({
  medications,
  checked,
  title,
  onClick,
  showDate = false,
}: {
  title?: string;
  medications: PatientMedication[];
  checked: BooleanMap;
  onClick: (medication: PatientMedication) => void;
  showDate?: boolean;
}) => {
  return (
    <div className="medications">
      <span className="medication-title">{title}</span>
      {medications.map((medication) => {
        const { value } = medication;
        const { prescription_medication } = value;
        const disable_refills = prescription_medication?.disable_refills;
        let key = value?.name || prescription_medication?.name || '';

        const [stillHasMedication] = CPPUtils.medicationStillLasts(medication, -3);

        const canRefill = !disable_refills && !stillHasMedication;

        const date =
          showDate && value?.date ? CalendarUtils.format(value.date, 'DD MMMM YYYY') : undefined;
        return (
          <div key={key}>
            <div className={'medication ' + (canRefill ? '' : ' disabled')}>
              <CheckGreen
                name={value.name}
                secondLine={date}
                selected={!!checked[key]}
                onChange={() => onClick(medication)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SuccessfulScreen = ({ onClick }: { onClick: () => void }) => {
  return (
    <ConfirmationContent
      title="Prescription Refill"
      text="Your refill request has been submitted. You will be notified when it has been approved by your practitioner."
      icon="circle-check"
      onConfirm={onClick}
      confirmLabel="Done"
    />
  );
};

PrescriptionRefill.propTypes = {
  prescription: PropTypes.object.isRequired,
};

export default PrescriptionRefill;
